/**
 * Sidebar Component
 *
 * A vertical navigation component with support for icons, badges, and collapsible groups.
 * Designed to work with AppShell for responsive behavior.
 *
 * @fileoverview Navigation sidebar component for the multi-tenant UI
 *
 * @example
 * // Basic usage with navigation items
 * const navItems = [
 *   { id: 'dashboard', label: 'Dashboard', icon: MdDashboard, path: '/' },
 *   { id: 'users', label: 'Users', icon: MdPeople, path: '/users', badge: 5 },
 * ];
 *
 * <Sidebar
 *   items={navItems}
 *   currentPath="/"
 *   onNavigate={(path) => navigate(path)}
 * />
 *
 * @example
 * // With collapsible groups
 * const navItems = [
 *   { id: 'dashboard', label: 'Dashboard', path: '/' },
 *   {
 *     id: 'settings',
 *     label: 'Settings',
 *     icon: MdSettings,
 *     children: [
 *       { id: 'profile', label: 'Profile', path: '/settings/profile' },
 *       { id: 'security', label: 'Security', path: '/settings/security' },
 *     ],
 *   },
 * ];
 *
 * <Sidebar items={navItems} initialExpandedGroups={['settings']} />
 */

import React, { useState, useCallback, type ReactNode } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  IconButton,
  Icon,
  Text,
  Badge,
  Collapse,
  Tooltip,
  useColorModeValue,
  type BoxProps,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import { ThemeToggle } from '../ThemeToggle';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Icon type that can be used in navigation items
 * Supports Chakra icons and react-icons
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type IconType = React.ComponentType<any>;

/**
 * Navigation item configuration
 */
export interface NavigationItem {
  /** Unique identifier for the item */
  id: string;
  /** Display label */
  label: string;
  /** Optional icon component (react-icons or Chakra icons) */
  icon?: IconType;
  /** Navigation path (required unless item has children) */
  path?: string;
  /** Optional badge content (number or string) */
  badge?: number | string;
  /** Badge color scheme (default: 'red') */
  badgeColorScheme?: string;
  /** Child items for collapsible groups */
  children?: NavigationItem[];
  /** Whether the item is disabled */
  disabled?: boolean;
}

/**
 * Props for the Sidebar component
 */
export interface SidebarProps extends Omit<BoxProps, 'children'> {
  /** Array of navigation items to display */
  items: NavigationItem[];
  /** Currently active path (used for highlighting) */
  currentPath?: string;
  /** Callback when a navigation item is clicked */
  onNavigate?: (path: string, item: NavigationItem) => void;
  /** Whether the sidebar is in collapsed mode (icons only) */
  isCollapsed?: boolean;
  /** Optional header content above navigation */
  header?: ReactNode;
  /** Optional footer content below navigation */
  footer?: ReactNode;
  /** Show tooltips when collapsed (default: true) */
  showTooltipsWhenCollapsed?: boolean;
  /** Initially expanded group IDs */
  initialExpandedGroups?: string[];
  /**
   * When provided, renders a collapse/expand toggle button in the footer
   * next to the theme toggle. Desktop-only — callers should omit this on
   * mobile, where the sidebar is a drawer rather than a collapsible rail.
   */
  onToggleCollapse?: () => void;
}

// ============================================================================
// Component Implementation
// ============================================================================

/**
 * Sidebar - Vertical navigation component
 *
 * Features:
 * - Navigation items with icons and badges
 * - Collapsible groups for nested navigation
 * - Active state highlighting based on current path
 * - Collapsed mode (icons only) with tooltips
 * - Header and footer slots for custom content
 * - Full dark mode support
 *
 * @param props - Component props (see SidebarProps interface)
 * @returns The rendered Sidebar component
 */
export function Sidebar({
  items,
  currentPath = '',
  onNavigate,
  isCollapsed = false,
  header,
  footer,
  showTooltipsWhenCollapsed = true,
  initialExpandedGroups = [],
  onToggleCollapse,
  ...boxProps
}: SidebarProps): React.ReactElement {
  // -------------------------------------------------------------------------
  // State Management
  // -------------------------------------------------------------------------

  /**
   * Track which groups are expanded
   */
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(initialExpandedGroups)
  );

  // -------------------------------------------------------------------------
  // Callbacks
  // -------------------------------------------------------------------------

  /**
   * Toggle a group's expanded state
   */
  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  /**
   * Handle navigation item click
   */
  const handleItemClick = useCallback(
    (item: NavigationItem) => {

      // Don't navigate if item is disabled
      if (item.disabled) return;

      // If item has children, toggle the group instead. When collapsed the
      // <Collapse> below is never rendered, so this is currently a no-op —
      // acceptable today since no caller (see navItems.ts) defines an item
      // with children, but a future grouped item would need a flyout menu
      // here instead of an inert toggle.
      if (item.children && item.children.length > 0) {
        toggleGroup(item.id);
        return;
      }

      // Navigate if path exists
      if (item.path && onNavigate) {

        onNavigate(item.path, item);
      } else {
          console.warn('Sidebar: No path or handler', { path: item.path, hasHandler: !!onNavigate });
      }
    },
    [onNavigate, toggleGroup]
  );

  /**
   * Check if a path is currently active
   */
  const isActive = useCallback(
    (path?: string): boolean => {
      if (!path) return false;
      // Exact match or starts with (for nested routes)
      return currentPath === path || currentPath.startsWith(`${path}/`);
    },
    [currentPath]
  );

  // -------------------------------------------------------------------------
  // Theming - Uses semantic tokens for color mode support
  // -------------------------------------------------------------------------

  // Background colors
  const bg = useColorModeValue('bg.surface', 'xacto.darkSurface');
  const hoverBg = useColorModeValue('gray.100', 'xacto.mutedNavy');
  const activeBg = useColorModeValue('blue.50', 'rgba(0, 217, 255, 0.1)');

  // Text colors
  const textColor = useColorModeValue('gray.600', 'xacto.silverGray');
  const activeTextColor = useColorModeValue('blue.600', 'xacto.electricCyan');
  const disabledTextColor = useColorModeValue('gray.400', 'xacto.mutedGray');

  // Border colors
  const activeBorderColor = useColorModeValue('blue.500', 'xacto.electricCyan');
  const headerFooterBorderColor = useColorModeValue('gray.200', 'xacto.mutedNavy');

  // -------------------------------------------------------------------------
  // Render Helpers
  // -------------------------------------------------------------------------

  /**
   * Renders a single navigation item
   */
  const renderNavItem = (item: NavigationItem, depth: number = 0): React.ReactElement => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedGroups.has(item.id);
    const itemIsActive = isActive(item.path);
    const isDisabled = item.disabled ?? false;

    // Calculate padding based on depth
    const paddingLeft = isCollapsed ? 4 : 4 + depth * 4;

    // Build the button content
    const buttonContent = (
      <HStack
        spacing={isCollapsed ? 0 : 3}
        justify={isCollapsed ? 'center' : 'flex-start'}
        w="full"
      >
        {/* Icon */}
        {item.icon && (
          <Icon
            as={item.icon}
            boxSize={5}
            color={itemIsActive ? activeTextColor : isDisabled ? disabledTextColor : textColor}
          />
        )}

        {/* Label (hidden when collapsed) */}
        {!isCollapsed && (
          <Text
            flex={1}
            fontSize="sm"
            fontWeight={itemIsActive ? '600' : '500'}
            color={itemIsActive ? activeTextColor : isDisabled ? disabledTextColor : textColor}
            textAlign="left"
            noOfLines={1}
          >
            {item.label}
          </Text>
        )}

        {/* Badge (hidden when collapsed) */}
        {!isCollapsed && item.badge !== undefined && (
          <Badge
            colorScheme={item.badgeColorScheme || 'red'}
            variant="solid"
            fontSize="xs"
            borderRadius="full"
            px={2}
          >
            {item.badge}
          </Badge>
        )}

        {/* Expand/Collapse indicator for groups */}
        {!isCollapsed && hasChildren && (
          <Icon
            as={isExpanded ? ChevronDownIcon : ChevronRightIcon}
            boxSize={4}
            color={textColor}
          />
        )}
      </HStack>
    );

    // Wrap in tooltip when collapsed
    const wrappedContent = isCollapsed && showTooltipsWhenCollapsed ? (
      <Tooltip label={item.label} placement="right" hasArrow>
        {buttonContent}
      </Tooltip>
    ) : (
      buttonContent
    );

    return (
      <Box key={item.id} w="full">
        {/* Navigation Button */}
        <Button
          variant="ghost"
          w="full"
          h="auto"
          py={3}
          pl={paddingLeft}
          pr={4}
          justifyContent={isCollapsed ? 'center' : 'flex-start'}
          borderRadius="md"
          bg={itemIsActive ? activeBg : 'transparent'}
          borderLeft={itemIsActive ? '3px solid' : '3px solid transparent'}
          borderColor={itemIsActive ? activeBorderColor : 'transparent'}
          _hover={{
            bg: isDisabled ? 'transparent' : itemIsActive ? activeBg : hoverBg,
          }}
          _disabled={{
            cursor: 'not-allowed',
            opacity: 0.6,
          }}
          isDisabled={isDisabled}
          onClick={() => handleItemClick(item)}
          aria-current={itemIsActive ? 'page' : undefined}
        >
          {wrappedContent}
        </Button>

        {/* Collapsible Children */}
        {hasChildren && !isCollapsed && (
          <Collapse in={isExpanded} animateOpacity>
            <VStack spacing={1} align="stretch" mt={1}>
              {item.children!.map((child) => renderNavItem(child, depth + 1))}
            </VStack>
          </Collapse>
        )}
      </Box>
    );
  };

  // -------------------------------------------------------------------------
  // Main Render
  // -------------------------------------------------------------------------

  return (
    <Box
      as="nav"
      bg={bg}
      h="full"
      display="flex"
      flexDirection="column"
      {...boxProps}
    >
      {/* Header Slot */}
      {header && (
        <Box p={4} borderBottom="1px" borderColor={headerFooterBorderColor}>
          {header}
        </Box>
      )}

      {/* Navigation Items */}
      <VStack
        flex={1}
        spacing={1}
        align="stretch"
        py={4}
        px={2}
        overflowY="auto"
      >
        {items.map((item) => renderNavItem(item))}

      </VStack>

      {/* Footer Slot - with ThemeToggle + collapse toggle */}
      <Box p={4} borderTop="1px" borderColor={headerFooterBorderColor}>
        <HStack justify={isCollapsed ? 'center' : 'space-between'} w="full">
          {!isCollapsed && <Text fontSize="xs" color="text.muted">Theme</Text>}
          <ThemeToggle size="sm" />
        </HStack>
        {onToggleCollapse && (
          <Tooltip
            label={isCollapsed ? 'Expand sidebar (Ctrl+B)' : 'Collapse sidebar (Ctrl+B)'}
            placement="right"
            hasArrow
          >
            <IconButton
              mt={2}
              w="full"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!isCollapsed}
              icon={
                <Icon
                  as={ChevronLeftIcon}
                  boxSize={4}
                  transform={isCollapsed ? 'rotate(180deg)' : undefined}
                  transition="transform 0.2s ease-in-out"
                />
              }
              size="sm"
              variant="ghost"
              onClick={onToggleCollapse}
            />
          </Tooltip>
        )}
        {footer}
      </Box>
    </Box>
  );
}

// ============================================================================
// Exports
// ============================================================================

export default Sidebar;
